import { useEffect, useRef, useState, useCallback } from 'react'
import { useRoomContext, useLocalParticipant } from '@livekit/components-react'
import { Track, RoomEvent } from 'livekit-client'
import type { RemoteAudioTrack, RemoteTrackPublication } from 'livekit-client'
import {
  BANDS,
  createOutputEqNodes, readBands, applyCorrections,
  type OutputEqNodes,
} from './antiLarsenProcessor'

const INTERVAL = 100  // ms

export interface BandEnergy {
  label: string
  energy: number
}

export interface AntiLarsenStatus {
  gains: number[]      // 5 EQ corrections, 1 per band (0 or negative)
  bands: BandEnergy[]
}

const INITIAL_STATUS: AntiLarsenStatus = {
  gains: [0, 0, 0, 0, 0, 0, 0],
  bands: [],
}

export interface AudioPipelineConfig {
  enabled: boolean            // master enable — creates/destroys the Web Audio chain
  antiLarsenActive: boolean   // when false, EQs are bypassed (all gains = 0)
  remoteLevel: number         // 0-10 (5 = unity gain 1.0)
  outputLevel: number         // 0-10 (5 = unity gain 1.0)
}

function levelToGain(level: number): number {
  return level / 5  // 0→0.0, 5→1.0, 10→2.0
}

/**
 * Audio pipeline — anti-Larsen limiter + remote/output gain control.
 *
 * Chain: RemoteAudioTrack → remoteGain → analyser → eq[0-4] → outputGain → speakers
 */
export function useAntiLarsen(config: AudioPipelineConfig) {
  const { enabled, antiLarsenActive, remoteLevel, outputLevel } = config
  const room = useRoomContext()
  const { localParticipant } = useLocalParticipant()

  const [trackVersion, setTrackVersion] = useState(0)
  const [status, setStatus] = useState<AntiLarsenStatus>(INITIAL_STATUS)

  const nodesRef = useRef<OutputEqNodes | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const attachedSidsRef = useRef<Set<string>>(new Set())
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Re-attach when remote tracks change
  useEffect(() => {
    if (!room) return
    const bump = () => setTrackVersion(v => v + 1)
    room.on(RoomEvent.TrackSubscribed, bump)
    room.on(RoomEvent.TrackUnsubscribed, bump)
    return () => {
      room.off(RoomEvent.TrackSubscribed, bump)
      room.off(RoomEvent.TrackUnsubscribed, bump)
    }
  }, [room])

  function getRemoteAudioTracks(): RemoteAudioTrack[] {
    const tracks: RemoteAudioTrack[] = []
    if (!room?.remoteParticipants) return tracks
    for (const [, rp] of room.remoteParticipants) {
      for (const [, pub] of rp.trackPublications) {
        const p = pub as RemoteTrackPublication
        if (p.track && p.kind === Track.Kind.Audio && p.isSubscribed) {
          tracks.push(p.track as RemoteAudioTrack)
        }
      }
    }
    return tracks
  }

  // Attach nodes + start limiter loop
  useEffect(() => {
    if (!enabled) {
      cleanup()
      setStatus(INITIAL_STATUS)
      return
    }

    const remoteTracks = getRemoteAudioTracks()
    if (remoteTracks.length === 0) return

    if (!ctxRef.current) {
      ctxRef.current = new AudioContext()
      ctxRef.current.resume()
    }
    if (!nodesRef.current) {
      nodesRef.current = createOutputEqNodes(ctxRef.current)
    }

    const nodes = nodesRef.current

    for (const rt of remoteTracks) {
      const sid = rt.sid ?? ''
      if (sid && !attachedSidsRef.current.has(sid)) {
        try {
          rt.setAudioContext(ctxRef.current!)
          // Chain: remoteGain → analyser → eq0-4 → outputGain
          rt.setWebAudioPlugins([nodes.remoteGain, nodes.analyser, ...nodes.eqs, nodes.outputGain])
          attachedSidsRef.current.add(sid)
          console.log('[audio-pipeline] Attached to remote track', sid)
        } catch (err) {
          console.warn('[audio-pipeline] Failed to attach:', sid, err)
        }
      }
    }

    startLoop()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, trackVersion])

  // Update remote gain when level changes
  useEffect(() => {
    if (nodesRef.current) {
      nodesRef.current.remoteGain.gain.value = levelToGain(remoteLevel)
    }
  }, [remoteLevel])

  // Update output gain when level changes
  useEffect(() => {
    if (nodesRef.current) {
      nodesRef.current.outputGain.gain.value = levelToGain(outputLevel)
    }
  }, [outputLevel])

  // Keep tick in a ref so setInterval always calls the latest version
  const tickRef = useRef<() => void>(() => {})
  tickRef.current = () => {
    const nodes = nodesRef.current
    if (!nodes) return

    // When muted (outputLevel=0), show zeroed bands instead of stale analyser data
    if (outputLevel === 0) {
      for (const eq of nodes.eqs) { eq.gain.value = 0 }
      setStatus({ gains: new Array(nodes.eqs.length).fill(0), bands: BANDS.map(b => ({ label: b.label, energy: -Infinity })) })
      return
    }

    const bands = readBands(nodes)

    let gains: number[]
    if (antiLarsenActive) {
      gains = applyCorrections(nodes, bands)
    } else {
      for (const eq of nodes.eqs) {
        eq.gain.value = 0
      }
      gains = new Array(nodes.eqs.length).fill(0)
    }

    setStatus({ gains, bands })
  }

  function startLoop() {
    if (timerRef.current) return
    timerRef.current = setInterval(() => tickRef.current(), INTERVAL)
  }

  function stopLoop() {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  function cleanup() {
    stopLoop()
    const remoteTracks = getRemoteAudioTracks()
    for (const rt of remoteTracks) {
      try { rt.setWebAudioPlugins([]) } catch { /* */ }
    }
    attachedSidsRef.current.clear()
    nodesRef.current = null
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {})
      ctxRef.current = null
    }
  }

  return status
}
